import { EpubCFI } from "../epub";
import type { CfiData } from "../epub/cfi/cfi";

/** An element anchor is reached when the visible text starts inside it. */
export const isElementCfiContainingPosition = (
  element: CfiData,
  position: CfiData,
): boolean =>
  !element.range &&
  element.path.terminal.offset === null &&
  element.path.steps[element.path.steps.length - 1]?.type === "element" &&
  element.spinePos === position.spinePos &&
  element.path.steps.every(
    (step, index) =>
      step.type === position.path.steps[index]?.type &&
      step.index === position.path.steps[index]?.index,
  );

export type CfiRangeEndpointOrder = -1 | 0 | 1;

export const getCfiRangeEndpointOrder = (
  cfiRange: string | undefined | null,
): CfiRangeEndpointOrder | null => {
  if (!cfiRange || !EpubCFI.prototype.isCfiString(cfiRange)) {
    return null;
  }

  try {
    const parsed = new EpubCFI(cfiRange);
    if (!parsed.range) {
      return 0;
    }

    const start = new EpubCFI(cfiRange);
    start.collapse(true);
    const end = new EpubCFI(cfiRange);
    end.collapse(false);

    return EpubCFI.prototype.compare(start, end);
  } catch {
    return null;
  }
};

export const isCfiRangeEndpointOrderValid = (
  cfiRange: string | undefined | null,
): boolean => {
  const order = getCfiRangeEndpointOrder(cfiRange);
  return order !== null && order !== 1;
};

const rangeHasRenderableContent = (range: Range | null): boolean => {
  if (!range) {
    return false;
  }
  if (!range.collapsed && range.toString().trim().length > 0) {
    return true;
  }
  try {
    return !range.collapsed && range.getClientRects().length > 0;
  } catch {
    return false;
  }
};

const resolveRenderableCfiRange = (
  cfiRange: string,
  iframeDoc: Document,
  ignoreClass?: string,
): Range | null => {
  try {
    const range = new EpubCFI(cfiRange).toRange(iframeDoc, ignoreClass);
    return rangeHasRenderableContent(range) ? range : null;
  } catch {
    return null;
  }
};

export const isCfiRangeEndpointOrderValidForDocument = (
  cfiRange: string | undefined | null,
  iframeDoc: Document | undefined,
  ignoreClass = "epubjs-ignore",
): boolean => {
  const order = getCfiRangeEndpointOrder(cfiRange);
  if (order === null) {
    return false;
  }
  if (order !== 1) {
    return true;
  }
  if (!cfiRange || !iframeDoc) {
    return false;
  }

  // Older CFIs generated for selections crossing inline markup can compare as
  // reversed because the stored text endpoint used a pre-interleaved text index.
  // If the browser can still resolve that CFI to a real forward DOM Range, keep
  // it renderable instead of hiding the persisted highlight.
  return !!(
    resolveRenderableCfiRange(cfiRange, iframeDoc, ignoreClass) ||
    resolveRenderableCfiRange(cfiRange, iframeDoc)
  );
};
